import { Box, createStyles, Stack } from '@mantine/core';
import type {
	AISetting,
	Update as AutomationActionUpdate,
	CatalogRule,
	PropagateField,
	PropagateTo,
	ScheduleSettings,
	ScheduleType,
} from '@repo/api-codegen';
import {
	useApiUpdateAutomationActionPropagateMetadata,
	useApiUpdateAutomationActionSendAnnouncement,
	useApiUpdateAutomationActionSendEmail,
	useApiUpdateAutomationActionSendSlack,
	useApiUpdateAutomationActionUpdateEntities,
	useApiUpdateAutomationAiSetting,
	useApiUpdateAutomationEnableAiOutputReview,
	useApiUpdateAutomationFilterSettings,
	useApiUpdateAutomationScheduleSettings,
	useApiUpdateAutomationShouldOverwriteMetadata,
	useApiUpdateAutomationTriggerType,
	useListCustomProperties,
} from '@repo/api-codegen';
import { SettingsButton } from '@repo/common/components';
import { FILTER_OPTIONS_DIVIDER } from '@repo/common/components/Filter/constants';
import type {
	FilterOption,
	FilterOptionType,
} from '@repo/common/components/Filter/types';
import {
	Badge,
	Banner,
	ResourceIcon,
	Title,
	type IconNames,
} from '@repo/foundations';
import { noop } from 'lodash-es';
import { observer } from 'mobx-react-lite';
import { useCallback, useMemo } from 'react';
import { AiOutputBlockController } from '../../features/AiOutputBlock/AiOutputBlockController';
import { EditBlockController } from '../../features/EditBlock/EditBlockController';
import { FilterBlockController } from '../../features/FilterBlock';
import { PropagateBlockController } from '../../features/PropagateBlock/PropagateBlockController';
import type { PropagateMetadataSettings } from '../../features/PropagateBlock/types';
import { ScheduleBlockController } from '../../features/ScheduleBlock';
import {
	ActionBlockType,
	TriggerBlockType,
} from '../../pages/AutomationPage/V2/store';
import { useAutomationV2Store } from '../../pages/AutomationPage/V2/useAutomationV2Store';
import { useFeatureFlags } from '../../utils/featureFlags';
import { BlockPlaceholder } from '../BlockPlaceholder/BlockPlaceholder';
import { FILTER_OPTIONS_CONFIG } from '../Filter/constants';
import { getCustomPropertyAsFilter } from '../Filter/customPropertyUtils';
import { openDeleteConfirmModal } from '../ModalManager';
import { NotificationBlock } from '../NotificationBlock/NotificationBlock';
import type { NotificationSettings } from '../NotificationBlock/types';
import { NotificationType } from '../NotificationBlock/types';
import type { AutomationSectionGroupNames } from './constants';
import {
	AutomationSectionType,
	DEFAULT_FILTER_SETTINGS,
	DEFAULT_NOTIFICATION_SETTINGS,
	DEFAULT_PROPAGATE_METADATA_SETTINGS,
	OPTION_TYPES,
} from './constants';
import SchemaChangeBlock from './SchemaChangeBlock';

export interface AutomationSectionOption {
	iconName: IconNames;
	title: string;
	onClick: () => void;
	isBeta?: boolean;
	groupName: AutomationSectionGroupNames;
	description?: string;
	aiIcon?: boolean;
}

interface AutomationSectionProps {
	title: string;
	options: AutomationSectionOption[];
	sectionType: AutomationSectionType;
	canAddMoreOptions?: boolean;
}

const useStyles = createStyles(
	(theme, { canAddMoreOptions }: { canAddMoreOptions: boolean }) => ({
		stack: {
			gap: theme.spacing.md,
			paddingTop: 0,
			paddingBottom: canAddMoreOptions
				? theme.spacing.md
				: theme.spacing['4xl'],
			maxWidth: theme.other.space[160],
			width: '100%',
		},
		aiIcon: {
			backgroundColor: theme.other.getColor('fill/ai-secondary/default'),
			color: theme.other.getColor('icon/ai/default'),
			'&:active': {
				backgroundColor: theme.other.getColor('fill/ai-secondary/active'),
			},
			'&[data-active]': {
				backgroundColor: theme.other.getColor('fill/ai-secondary/active'),
			},
		},
		triggerBanner: {
			padding: `${theme.spacing.md} ${theme.spacing.sm}`,
			border: `1px solid ${theme.other.getColor('border/secondary/default')}`,
			borderTop: 'none',
			borderBottomLeftRadius: theme.radius.md,
			borderBottomRightRadius: theme.radius.md,
		},
	})
);

export const AutomationSection = observer(
	({
		title,
		options,
		sectionType,
		canAddMoreOptions = false,
	}: AutomationSectionProps) => {
		const { classes } = useStyles({ canAddMoreOptions });
		const store = useAutomationV2Store();

		const { mutateAsync: updateTriggerType } =
			useApiUpdateAutomationTriggerType();
		const { mutateAsync: updateScheduleSettings } =
			useApiUpdateAutomationScheduleSettings();
		const { mutateAsync: updateSlackMessage } =
			useApiUpdateAutomationActionSendSlack();
		const { mutateAsync: updateEmail } =
			useApiUpdateAutomationActionSendEmail();
		const { mutateAsync: updateAnnouncement } =
			useApiUpdateAutomationActionSendAnnouncement();
		const { mutateAsync: updateFilterSettings } =
			useApiUpdateAutomationFilterSettings();
		const { mutateAsync: updateAiSettings } = useApiUpdateAutomationAiSetting();
		const { mutateAsync: updateActionPropagateMetadata } =
			useApiUpdateAutomationActionPropagateMetadata();
		const { mutateAsync: updateActionUpdateEntities } =
			useApiUpdateAutomationActionUpdateEntities();
		const { mutateAsync: updateShouldOverwriteMetadata } =
			useApiUpdateAutomationShouldOverwriteMetadata();
		const { mutateAsync: updateEnableAIOutputReview } =
			useApiUpdateAutomationEnableAiOutputReview();

		const { customPropertiesFilter } = useFeatureFlags();

		const { data: customProperties } = useListCustomProperties(
			{},
			{
				enabled: customPropertiesFilter,
			}
		);

		const filterOptions: (FilterOption | typeof FILTER_OPTIONS_DIVIDER)[] =
			useMemo(() => {
				const baseOptions: (FilterOption | typeof FILTER_OPTIONS_DIVIDER)[] =
					OPTION_TYPES.map((option) =>
						option === FILTER_OPTIONS_DIVIDER
							? FILTER_OPTIONS_DIVIDER
							: FILTER_OPTIONS_CONFIG[option as FilterOptionType]
					);

				if (customPropertiesFilter && !!customProperties?.length) {
					baseOptions.push(FILTER_OPTIONS_DIVIDER);
					baseOptions.push(
						...(customProperties
							.map(getCustomPropertyAsFilter)
							.filter(Boolean) as FilterOption[])
					);
				}

				return baseOptions;
			}, [customPropertiesFilter, customProperties]);

		const optionsVisible = useMemo(() => {
			if (sectionType === AutomationSectionType.TRIGGER) {
				return !store.triggerBlockType;
			}

			if (sectionType === AutomationSectionType.ACTION) {
				return !store.hasActionBlocks;
			}

			return false;
		}, [sectionType, store.triggerBlockType, store.hasActionBlocks]);

		const requiresTrigger =
			sectionType === AutomationSectionType.TRIGGER &&
			store.actionBlocks.length > 0 &&
			!store.triggerBlockType;

		const handleScheduleSettingsChange = useCallback(
			(scheduleSettings: ScheduleSettings) => {
				if (store.automation) {
					updateScheduleSettings({
						pathParams: { automationId: store.automation.id },
						body: {
							schedule_settings: {
								schedule_type: scheduleSettings.schedule_type,
								hour_of_day: scheduleSettings.hour_of_day,
								day_of_week: scheduleSettings.day_of_week,
								cron_expression: scheduleSettings.cron_expression,
							},
						},
					});
				}
			},
			[updateScheduleSettings, store]
		);

		const handleEditResourcesChange = useCallback(
			(editSettings: AutomationActionUpdate[]) => {
				if (store.automation?.id) {
					const toSave = editSettings
						.filter((s) => !s.is_ai_edit_action)
						.map((editSetting) => ({
							...editSetting,
							is_ai_edit_action: false,
						}));
					updateActionUpdateEntities({
						pathParams: { automationId: store.automation.id },
						body: { action_update_entities: toSave },
					});
				}
			},
			[store, updateActionUpdateEntities]
		);

		const handleUpdateShouldOverwriteMetadata = useCallback(
			(shouldOverwriteMetadata: boolean) => {
				if (store.automation?.id) {
					updateShouldOverwriteMetadata({
						pathParams: { automationId: store.automation.id },
						body: { should_overwrite_metadata: shouldOverwriteMetadata },
					});
				}
			},
			[store, updateShouldOverwriteMetadata]
		);

		const handleUpdateEnableAIOutputReview = useCallback(
			(enableAIOutputReview: boolean) => {
				if (store.automation?.id) {
					updateEnableAIOutputReview({
						pathParams: { automationId: store.automation.id },
						body: { enable_ai_output_review: enableAIOutputReview },
					});
				}
			},
			[store, updateEnableAIOutputReview]
		);

		const handleTriggerDelete = useCallback(() => {
			if (store.automation) {
				updateTriggerType({
					pathParams: { automationId: store.automation.id },
					body: { trigger_type: null },
				});
			}
		}, [updateTriggerType, store]);

		const filterSettings = useMemo((): CatalogRule => {
			if (store.automation?.filter_settings) {
				return store.automation.filter_settings;
			}

			return DEFAULT_FILTER_SETTINGS;
		}, [store.automation?.filter_settings]);

		const scheduleSettings = useMemo((): ScheduleSettings => {
			if (store.automation?.schedule_settings) {
				return {
					schedule_type: store.automation.schedule_settings
						.schedule_type as ScheduleType,
					hour_of_day: store.automation.schedule_settings.hour_of_day,
					day_of_week: store.automation.schedule_settings.day_of_week,
					cron_expression: store.automation.schedule_settings.cron_expression,
				};
			}

			return {
				schedule_type: 'weekly',
				hour_of_day: 0,
				day_of_week: 0,
			};
		}, [store.automation?.schedule_settings]);

		const aiSettings = useMemo((): AISetting[] => {
			if (store.automation?.ai_settings) {
				return store.automation.ai_settings;
			}
			return [];
		}, [store.automation?.ai_settings]);

		const slackSettings = useMemo((): NotificationSettings => {
			if (store.automation?.action_send_slack_message) {
				return {
					message: store.automation.action_send_slack_message.body,
					channels: store.automation.action_send_slack_message.channels,
					includeSummary:
						store.automation.action_send_slack_message.include_summary,
				};
			}

			return DEFAULT_NOTIFICATION_SETTINGS;
		}, [store.automation?.action_send_slack_message]);

		const emailSettings = useMemo((): NotificationSettings => {
			if (store.automation?.action_send_email) {
				const recipients = [];
				store.automation.action_send_email.users?.forEach((user) => {
					recipients.push(user.id);
				});

				store.automation.action_send_email.user_groups?.forEach((group) => {
					recipients.push(group.id);
				});

				if (store.automation.action_send_email.owners) {
					recipients.push('owners');
				}

				if (store.automation.action_send_email.subscribers) {
					recipients.push('subscribers');
				}

				if (store.automation.action_send_email.external_emails) {
					recipients.push(
						...store.automation.action_send_email.external_emails
					);
				}

				return {
					message: store.automation.action_send_email.body,
					subject: store.automation.action_send_email.subject,
					includeSummary: store.automation.action_send_email.include_summary,
					recipients,
					users: store.automation.action_send_email.users,
					user_groups: store.automation.action_send_email.user_groups,
				};
			}

			return DEFAULT_NOTIFICATION_SETTINGS;
		}, [store.automation?.action_send_email]);

		const announcementSettings = useMemo((): NotificationSettings => {
			if (store.automation?.action_send_announcement) {
				const recipients = [];
				store.automation.action_send_announcement.users?.forEach((user) => {
					recipients.push(user.id);
				});

				store.automation.action_send_announcement.user_groups?.forEach(
					(group) => {
						recipients.push(group.id);
					}
				);

				if (store.automation.action_send_announcement.owners) {
					recipients.push('owners');
				}

				if (store.automation.action_send_announcement.subscribers) {
					recipients.push('subscribers');
				}

				return {
					message: store.automation.action_send_announcement.body,
					subject: store.automation.action_send_announcement.subject,
					includeSummary:
						store.automation.action_send_announcement.include_summary,
					recipients,
					users: store.automation.action_send_announcement.users,
					user_groups: store.automation.action_send_announcement.user_groups,
				};
			}

			return DEFAULT_NOTIFICATION_SETTINGS;
		}, [store.automation?.action_send_announcement]);

		const propagateMetadataSettings = useMemo((): PropagateMetadataSettings => {
			if (store.automation?.action_propagate_metadata) {
				return {
					overrideExistingProperties:
						store.automation.action_propagate_metadata?.overwrite_metadata ??
						false,
					properties: store.automation.action_propagate_metadata?.fields ?? [],
					propagateTo: store.automation.action_propagate_metadata?.to ?? '',
					level:
						store.automation.action_propagate_metadata?.levels?.toString() ??
						'',
				};
			}

			return DEFAULT_PROPAGATE_METADATA_SETTINGS;
		}, [store.automation?.action_propagate_metadata]);

		const editResourcesSettings = useMemo((): AutomationActionUpdate[] => {
			if (store.automation?.action_update_entities) {
				return store.automation.action_update_entities;
			}
			return [];
		}, [store.automation?.action_update_entities]);

		const shouldOverwriteMetadata = useMemo((): boolean => {
			if (store.automation?.should_overwrite_metadata) {
				return store.automation.should_overwrite_metadata;
			}
			return false;
		}, [store.automation?.should_overwrite_metadata]);

		const enableAIOutputReview = useMemo((): boolean => {
			if (store.automation?.enable_ai_output_review) {
				return store.automation.enable_ai_output_review;
			}
			return false;
		}, [store.automation?.enable_ai_output_review]);

		const isScheduleBlockVisible =
			sectionType === AutomationSectionType.TRIGGER &&
			store.triggerBlockType === TriggerBlockType.SCHEDULE &&
			!optionsVisible;

		const isSchemaChangeBlockVisible =
			sectionType === AutomationSectionType.TRIGGER &&
			store.triggerBlockType === TriggerBlockType.SCHEMA &&
			!optionsVisible;

		const isFilterResourcesBlockVisible =
			sectionType === AutomationSectionType.ACTION &&
			!optionsVisible &&
			store.isFilterResourcesBlockVisible;

		const isFilterBlockPlaceholderVisible =
			sectionType === AutomationSectionType.ACTION &&
			!optionsVisible &&
			store.isFilterBlockRequired;

		const isGenerateAiOutputBlockVisible =
			sectionType === AutomationSectionType.ACTION &&
			!optionsVisible &&
			store.isGenerateAiOutputBlockVisible;

		const isEditResourcesBlockVisible =
			sectionType === AutomationSectionType.ACTION &&
			!optionsVisible &&
			(store.isEditResourcesBlockVisible || aiSettings.length > 0);

		const isEditResourcesBlockPlaceholderVisible =
			sectionType === AutomationSectionType.ACTION &&
			!optionsVisible &&
			store.isEditResourcesBlockRequired &&
			aiSettings.length === 0;

		const isPropagateMetadataBlockVisible =
			sectionType === AutomationSectionType.ACTION &&
			!optionsVisible &&
			store.isPropagateMetadataBlockVisible;

		const isSendAnnouncementBlockVisible =
			sectionType === AutomationSectionType.ACTION &&
			!optionsVisible &&
			store.isSendAnnouncementBlockVisible;

		const isSendSlackMessageBlockVisible =
			sectionType === AutomationSectionType.ACTION &&
			!optionsVisible &&
			store.isSendSlackMessageBlockVisible;

		const isSendEmailBlockVisible =
			sectionType === AutomationSectionType.ACTION &&
			!optionsVisible &&
			store.isSendEmailBlockVisible;

		// Delete Actions
		const handlePropagateMetadataDelete = useCallback(() => {
			if (store.automation?.id) {
				updateActionPropagateMetadata({
					pathParams: { automationId: store.automation.id },
					body: { action_propagate_metadata: null },
				});

				store.removeActionBlockType(ActionBlockType.PROPAGATE_METADATA);
			}
		}, [store, updateActionPropagateMetadata]);

		const handleAiOutputBlockDelete = useCallback(() => {
			if (filterSettings.operands.length > 0) {
				openDeleteConfirmModal({
					title: 'Delete AI output?',
					description: "This can't be undone",
					onConfirm: async () => {
						if (store.automation?.id) {
							updateAiSettings({
								pathParams: { automationId: store.automation.id },
								body: { ai_settings: null },
							});
						}
						store.setHasAiOutput(false);
						store.removeActionBlockType(ActionBlockType.GENERATE_AI_OUTPUT);
					},
				});
			} else {
				if (store.automation?.id) {
					updateAiSettings({
						pathParams: { automationId: store.automation.id },
						body: { ai_settings: null },
					});
				}
				store.setHasAiOutput(false);
				store.removeActionBlockType(ActionBlockType.GENERATE_AI_OUTPUT);
			}
		}, [store, updateAiSettings, filterSettings.operands.length]);

		const handleEditResourcesDelete = useCallback(() => {
			if (store.automation?.id) {
				updateActionUpdateEntities({
					pathParams: { automationId: store.automation.id },
					body: { action_update_entities: [] },
				});
			}
			store.removeActionBlockType(ActionBlockType.EDIT_RESOURCES);
		}, [store, updateActionUpdateEntities]);

		const handleFilterResourcesDelete = useCallback(() => {
			if (store.automation?.id) {
				updateFilterSettings({
					pathParams: { automationId: store.automation.id },
					body: { filter_settings: null },
				});
			}
			store.removeActionBlockType(ActionBlockType.FILTER_RESOURCES);
		}, [store, updateFilterSettings]);

		const handleEmailNotificationDelete = useCallback(() => {
			if (store.automation?.id) {
				updateEmail({
					pathParams: { automationId: store.automation.id },
					body: { action_send_email: null },
				});
			}
			store.removeActionBlockType(ActionBlockType.SEND_EMAIL);
		}, [store, updateEmail]);

		const handleSlackNotificationDelete = useCallback(() => {
			if (store.automation?.id) {
				updateSlackMessage({
					pathParams: { automationId: store.automation.id },
					body: { action_send_slack_message: null },
				});
			}
			store.removeActionBlockType(ActionBlockType.SEND_SLACK_MESSAGE);
		}, [store, updateSlackMessage]);

		const handleAnnouncementNotificationDelete = useCallback(() => {
			if (store.automation?.id) {
				updateAnnouncement({
					pathParams: { automationId: store.automation.id },
					body: { action_send_announcement: null },
				});
			}
			store.removeActionBlockType(ActionBlockType.SEND_ANNOUNCEMENT);
		}, [store, updateAnnouncement]);

		// Update Actions
		const handleUpdateSlackMessage = useCallback(
			(slackMessage: NotificationSettings) => {
				if (store.automation?.id) {
					updateSlackMessage({
						pathParams: { automationId: store.automation.id },
						body: {
							action_send_slack_message: {
								body: slackMessage.message,
								channels: slackMessage.channels,
								include_summary: slackMessage.includeSummary ?? false,
							},
						},
					});
				}
			},
			[store, updateSlackMessage]
		);

		const handleUpdateEmail = useCallback(
			(email: NotificationSettings) => {
				if (store.automation?.id) {
					updateEmail({
						pathParams: { automationId: store.automation.id },
						body: {
							action_send_email: {
								body: email.message,
								subject: email.subject,
								include_summary: email.includeSummary ?? false,
								users: email.users,
								user_groups: email.user_groups,
							},
						},
					});
				}
			},
			[store, updateEmail]
		);

		const handleUpdateAnnouncement = useCallback(
			(announcement: NotificationSettings) => {
				if (store.automation?.id) {
					updateAnnouncement({
						pathParams: { automationId: store.automation.id },
						body: {
							action_send_announcement: {
								body: announcement.message,
								subject: announcement.subject,
								include_summary: announcement.includeSummary ?? false,
								users: announcement.users,
								user_groups: announcement.user_groups,
							},
						},
					});
				}
			},
			[store, updateAnnouncement]
		);

		const handleUpdatePropagateMetadata = useCallback(
			(propagateMetadata: PropagateMetadataSettings) => {
				if (store.automation?.id) {
					updateActionPropagateMetadata({
						pathParams: { automationId: store.automation.id },
						body: {
							action_propagate_metadata: {
								to: propagateMetadata.propagateTo as PropagateTo,
								levels: Number(propagateMetadata.level),
								overwrite_metadata:
									propagateMetadata.overrideExistingProperties,
								fields: propagateMetadata.properties as PropagateField[],
							},
						},
					});
				}
			},
			[store, updateActionPropagateMetadata]
		);

		const handleUpdateFilterSettings = useCallback(
			(rule: CatalogRule) => {
				if (store.automation?.id && !store.isFilterBlockRequired) {
					updateFilterSettings({
						pathParams: { automationId: store.automation.id },
						body: { filter_settings: rule },
					});
				}
			},
			[store, updateFilterSettings]
		);

		const handleUpdateAiSettings = useCallback(
			(newAiSettings: AISetting[]) => {
				if (store.automation?.id) {
					updateAiSettings({
						pathParams: { automationId: store.automation.id },
						body: { ai_settings: newAiSettings },
					});

					store.setHasAiOutput(newAiSettings.length > 0);
				}
			},
			[store, updateAiSettings]
		);

		const groupedOptions = useMemo(() => {
			const groupMap: Record<string, AutomationSectionOption[]> = {};

			options.forEach((option) => {
				groupMap[option.groupName] = [
					...(groupMap[option.groupName] || []),
					option,
				];
			});

			return groupMap;
		}, [options]);

		return (
			<Stack className={classes.stack}>
				<Title size="md">{title}</Title>
				{optionsVisible && (
					<Stack>
						{Object.values(groupedOptions).map((optionList, index) => (
							<Box key={optionList[0].groupName}>
								{optionList.map((option) => (
									<SettingsButton
										key={option.title}
										title={option.title}
										description={option.description}
										variant="tertiary"
										onClick={noop}
										onClickRow={option.onClick}
										leftComponent={
											<ResourceIcon
												iconName={option.iconName}
												backgroundColor={
													option.aiIcon
														? 'fill/ai-secondary/default'
														: undefined
												}
												iconColor={
													option.aiIcon ? 'icon/ai/default' : undefined
												}
											/>
										}
										disabled={true}
										rightComponent={option.isBeta && <Badge>Beta</Badge>}
										className={option.aiIcon ? classes.aiIcon : undefined}
									/>
								))}
								{requiresTrigger &&
									index === Object.values(groupedOptions).length - 1 && (
										<Box className={classes.triggerBanner}>
											<Banner
												message="No trigger is set. This automation will only run when triggered manually."
												tone="info"
												inCard
											/>
										</Box>
									)}
							</Box>
						))}
					</Stack>
				)}
				{isScheduleBlockVisible && (
					<ScheduleBlockController
						initialScheduleSettings={scheduleSettings}
						onScheduleChange={handleScheduleSettingsChange}
						onScheduleDelete={handleTriggerDelete}
					/>
				)}
				{isSchemaChangeBlockVisible && (
					<SchemaChangeBlock onSchemaDelete={handleTriggerDelete} />
				)}
				{isFilterBlockPlaceholderVisible && (
					<BlockPlaceholder
						onClick={() =>
							store.addActionBlockType(ActionBlockType.FILTER_RESOURCES)
						}
						iconName="plus"
						title="Add filter action"
						bannerMessage="You'll need to add this action before continuing."
					/>
				)}
				{isFilterResourcesBlockVisible && (
					<FilterBlockController
						displayTitle
						onFilterResourcesDelete={handleFilterResourcesDelete}
						filterOptions={filterOptions}
						initialRule={filterSettings}
						onRuleChange={handleUpdateFilterSettings}
						variant="tertiary"
						limitSearch
						tooltip="Remove action"
						backgroundColor="fill/transparent-secondary/default"
						removeBottomRadius
						includeEmptyRule
					/>
				)}
				{isGenerateAiOutputBlockVisible && (
					<AiOutputBlockController
						key={JSON.stringify(filterSettings)}
						initialAiSettings={aiSettings}
						onAiSettingsChange={handleUpdateAiSettings}
						onAiOutputBlockDelete={handleAiOutputBlockDelete}
						filterRule={filterSettings}
						enableAIOutputReview={enableAIOutputReview}
						onEnableAIOutputReviewChange={handleUpdateEnableAIOutputReview}
					/>
				)}
				{isEditResourcesBlockPlaceholderVisible && (
					<BlockPlaceholder
						onClick={() =>
							store.addActionBlockType(ActionBlockType.EDIT_RESOURCES)
						}
						iconName="plus"
						title="Add edit resources action"
						bannerMessage="You'll need to add this action to complete your automation setup."
					/>
				)}
				{isEditResourcesBlockVisible && (
					<EditBlockController
						key={JSON.stringify(aiSettings)}
						initialEditSettings={editResourcesSettings}
						initialShouldOverwriteMetadata={shouldOverwriteMetadata}
						onEditChange={handleEditResourcesChange}
						onEditDelete={handleEditResourcesDelete}
						onShouldOverwriteMetadataChange={
							handleUpdateShouldOverwriteMetadata
						}
						aiSettings={aiSettings}
						tooltip="Remove action"
					/>
				)}
				{isPropagateMetadataBlockVisible && (
					<PropagateBlockController
						initialPropagateSettings={propagateMetadataSettings}
						onPropagateChange={handleUpdatePropagateMetadata}
						onPropagateDelete={handlePropagateMetadataDelete}
						tooltip="Remove action"
					/>
				)}
				{isSendAnnouncementBlockVisible && (
					<NotificationBlock
						notificationType={NotificationType.ANNOUNCEMENT}
						notificationSettings={announcementSettings}
						onNotificationSettingsChange={handleUpdateAnnouncement}
						title="Send announcement"
						iconName="speakerphone"
						onNotificationDelete={handleAnnouncementNotificationDelete}
						toolTip="Remove action"
					/>
				)}
				{isSendEmailBlockVisible && (
					<NotificationBlock
						notificationType={NotificationType.EMAIL}
						notificationSettings={emailSettings}
						onNotificationSettingsChange={handleUpdateEmail}
						title="Send email"
						iconName="mail"
						onNotificationDelete={handleEmailNotificationDelete}
						toolTip="Remove action"
					/>
				)}
				{isSendSlackMessageBlockVisible && (
					<NotificationBlock
						notificationType={NotificationType.SLACK}
						notificationSettings={slackSettings}
						onNotificationSettingsChange={handleUpdateSlackMessage}
						title="Send slack message"
						iconName="brandSlack"
						onNotificationDelete={handleSlackNotificationDelete}
						toolTip="Remove action"
					/>
				)}
			</Stack>
		);
	}
);
